import { IncidentCaseView } from '@features/logs/components/incidents/IncidentCaseView'
import IncidentCaseV2PdfButton from '@features/logs/components/incidents/v2/pdf/IncidentCaseV2PdfButton'
import IncidentCaseV2View from '@features/logs/components/incidents/v2/view/IncidentCaseV2View'
import { cssVars } from '@lmc/design-system/tokens'
import type { Home, IncidentReport } from '@lmc/ts-common'
import { Button } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { Fragment, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

type IncidentPdfButtonProps = {
  incidentReport: IncidentReport
  isV2: boolean
  isDownloading: boolean
  onDownload: () => void
  testId: string
}

export const IncidentPdfButton = ({
  incidentReport,
  isV2,
  isDownloading,
  onDownload,
  testId
}: IncidentPdfButtonProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  if (isV2) {
    return (
      <IncidentCaseV2PdfButton
        incidentReport={incidentReport}
        testId={testId}
      />
    )
  }
  return (
    <Button data-testid={testId} onClick={onDownload} isLoading={isDownloading}>
      {t(incidentReportsConstants.DOWNLOAD_PDF)}
    </Button>
  )
}

type IncidentCasePanelProps = {
  incidentReport: IncidentReport
  isV2: boolean
  hasCase: boolean
  severities: Home['incidentSeverity']
  testId: string
}

export const IncidentCasePanel = ({
  incidentReport,
  isV2,
  hasCase,
  severities,
  testId
}: IncidentCasePanelProps) => {
  if (isV2) return <IncidentCaseV2View incidentReport={incidentReport} />
  if (!hasCase) return null
  return (
    <IncidentCaseView
      testId={testId}
      incidentReport={incidentReport}
      severities={severities}
    />
  )
}

export const IncidentScreenContainer = ({
  isV2,
  children
}: {
  isV2: boolean
  children: ReactNode
}) => (isV2 ? <Screen>{children}</Screen> : <Fragment>{children}</Fragment>)

/** The v2 form's gutter, so the v2 view lines up with the form. */
const Screen = styled.div`
  box-sizing: border-box;
  margin-inline: auto;
  max-width: 1080px;
  padding-inline: ${cssVars.spacing[300]};

  @media (max-width: 768px) {
    padding-inline: ${cssVars.spacing[200]};
  }
`
